import { useEffect, useState } from 'react';
import { listExecutions, type ExecutionRow } from '../api';
import { useAsync } from '../lib/async';
import { FamilyFilter, NO_FAMILY, serverFamily } from '../lib/family';
import { replace } from '../lib/route';
import {
  Empty,
  Id,
  Link,
  Pill,
  Result,
  StatusPill,
  Toolbar,
  ago,
  span,
  when,
} from '../lib/ui';

/**
 * The orchestrator first: every run, joined to whatever commission its name
 * resolves to.
 *
 * The join is the execution NAME. `commissions.py` passes `name=commission_id`
 * to StartExecution, and `commissionId` is the partition key of
 * `showling-commissions`, so the correspondence is 1:1 and free. Execution
 * *tagging* — "tag invocations to the family" — is not an API that exists:
 * StartExecution accepts only input, name, stateMachineArn and traceHeader, and
 * tags belong to the state machine resource, of which there is one. The name
 * join is also the better tool, because it survives the state-machine rebuild
 * that this project treats as its normal recovery move, and a tag would not.
 *
 * What this screen is FOR, over the Pipeline screen, is disagreement. A row with
 * `join: "no-commission-row"` is an execution nothing else in the console can
 * explain — measured live: one exists, left behind when a canary family was torn
 * down and its commission deleted.
 */

const STATUSES = ['', 'RUNNING', 'SUCCEEDED', 'FAILED', 'TIMED_OUT', 'ABORTED', 'PENDING_REDRIVE'];

export function Executions({ initialStatus }: { initialStatus?: string }) {
  const [status, setStatus] = useState(initialStatus ?? '');
  const [family, setFamily] = useState('');

  useEffect(() => {
    replace({ name: 'executions', ...(status ? { status } : {}) });
  }, [status]);

  const familyParam = serverFamily(family);
  const { result, reload } = useAsync(
    () =>
      listExecutions({
        ...(status ? { status } : {}),
        ...(familyParam ? { familyId: familyParam } : {}),
      }),
    [status, familyParam ?? ''],
  );

  return (
    <>
      <Toolbar>
        <span className="group">
          <label htmlFor="exec-status">Status</label>
          <select id="exec-status" value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUSES.map((value) => (
              <option key={value || 'all'} value={value}>
                {value || 'any status'}
              </option>
            ))}
          </select>
        </span>
        <FamilyFilter value={family} onChange={setFamily} />
        <span className="spacer" />
        <button onClick={reload}>Refresh</button>
      </Toolbar>

      {/* Both refusals are covered, and they are different facts. `missing` is
          a route that was never deployed; the unavailable branch is the route
          answering 503 because this deployment cannot see executions at all.
          Only the second one is true today, and it used to render as a red
          retryable failure across the whole screen. */}
      <Result
        of={result}
        what="executions"
        retry={reload}
        missing="This build of the staff API has no /admin/pipeline/executions route."
        unavailableDetail={
          <>
            Every row on this screen comes from the workflow engine's own
            execution history, and this deployment exposes none — so there is
            nothing left to show. The Pipeline screen's commission table is a
            database read and still works: go there to see what exists and what
            state it claims to be in. This screen was the only one that could
            have told you where a run actually got to.{' '}
            {/* The refusal printed above names GET /admin/pipeline/sweep, which
                is deployed and which this console had no client for — so the
                instruction was a dead end on four screens at once. */}
            The money question it cannot answer is answered:{' '}
            <Link to={{ name: 'dashboard' }}>
              the stranded-hold sweep is on the Overview
            </Link>
            .
          </>
        }
      >
        {(page) => {
          // The sentinel is the one filter the API has no word for, and here it
          // means something specific and useful: executions with no commission
          // row, which is exactly the orphan case.
          const rows =
            family === NO_FAMILY
              ? page.executions.filter((row) => !row.familyId)
              : page.executions;
          const orphans = page.executions.filter((row) => row.join === 'no-commission-row');
          return (
            <>
              <p className="muted small">
                Showing {rows.length} of the most recent {page.limit ?? '?'} executions
                {page.statusFilter ? ` with status ${page.statusFilter}` : ''}.
                {page.truncated && (
                  <>
                    {' '}
                    <b className="warn">There is more history than this.</b> The
                    handler does not page further on purpose: paging execution
                    history is unbounded work behind a request with a timeout, and
                    it could spend the whole budget doing it. Narrow with the
                    status filter rather than walking backwards.
                  </>
                )}
              </p>
              {page.familyFilterAppliedLocally && (
                <p className="muted small">
                  The family filter was applied after the join, not by the
                  orchestrator — `statusFilter` is the only filter it offers.
                  So this is the family's rows <em>within</em> the most recent{' '}
                  {page.limit} executions, not their most recent {page.limit}.
                </p>
              )}
              {orphans.length > 0 && (
                <div className="notice notice-warn">
                  <b>
                    {orphans.length} execution{orphans.length === 1 ? '' : 's'} with
                    no commission row.
                  </b>
                  <span className="muted small">
                    The commission was deleted — teardown does this — or the
                    execution predates the row. Family and request are unknown, not
                    absent: open the detail to read the familyId out of the
                    execution input, which is the only place it survives.
                  </span>
                </div>
              )}

              {!rows.length ? (
                <Empty what="executions" filtered={Boolean(status || family)} />
              ) : (
                <table>
                  <thead>
                    <tr>
                      <th>Started</th>
                      <th>Execution</th>
                      <th>Family</th>
                      <th>Request</th>
                      <th>Ran for</th>
                      {/* "Run" and "Commission" rather than naming the service
                          that produced one of them: these two columns exist to
                          be compared, and a header naming a vendor made the
                          comparison read as cloud-versus-database instead of
                          run-versus-row. */}
                      <th>Run</th>
                      <th>Commission</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <Row key={row.executionArn ?? row.name} row={row} />
                    ))}
                  </tbody>
                </table>
              )}
            </>
          );
        }}
      </Result>
    </>
  );
}

function Row({ row }: { row: ExecutionRow }) {
  const orphan = row.join === 'no-commission-row';
  // The two status words disagree by design, and where they do it is the
  // interesting row: a SUCCEEDED execution against a `failed` commission is the
  // catcher having run. Marked rather than reconciled.
  const disagrees =
    !orphan && row.status === 'SUCCEEDED' && row.commissionStatus === 'failed';
  return (
    <tr className={orphan || disagrees ? 'flagged' : ''}>
      <td className="muted nowrap" title={when(row.startDate)}>
        {ago(row.startDate)}
      </td>
      <td>
        <Id value={row.name} chars={10} />
      </td>
      <td>
        {orphan ? (
          <Pill tone="bad" title={row.joinDetail}>
            unknown
          </Pill>
        ) : row.familyId ? (
          <Link to={{ name: 'families', familyId: row.familyId }}>
            {row.familyName || <Id value={row.familyId} />}
          </Link>
        ) : (
          <span className="muted">—</span>
        )}
        {row.familyExists === false && (
          <div className="warn small">family row is gone</div>
        )}
      </td>
      <td>
        <span className="clamp" title={row.request ?? undefined}>
          {row.request ?? <span className="muted">unknown</span>}
        </span>
      </td>
      <td className="muted nowrap">{row.durationSeconds ? span(row.durationSeconds) : ''}</td>
      <td>
        <StatusPill status={row.status} />
        {Boolean(row.redriveCount) && (
          <div className="warn small">redriven ×{row.redriveCount}</div>
        )}
      </td>
      <td>
        {orphan ? (
          <span className="muted">no row</span>
        ) : (
          <>
            <StatusPill status={row.commissionStatus} />
            {disagrees && (
              <div className="warn small" title="the Release the hold catcher ran and returned cleanly, so the run is satisfied while the commission failed">
                catcher ran
              </div>
            )}
          </>
        )}
      </td>
      <td>
        {row.commissionId && (
          <Link to={{ name: 'commission', commissionId: row.commissionId }}>open</Link>
        )}
      </td>
    </tr>
  );
}
